import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';

class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  // eslint-disable-next-line no-unused-vars
  static getDerivedStateFromError(_error) {
    // Update state so the next render will show the fallback UI
    // Note: error parameter is required by React API but unused in this implementation
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Update state with error details
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log error to monitoring service (Sentry integration)
    if (window.Sentry) {
      window.Sentry.withScope((scope) => {
        scope.setTag('component', 'AdminErrorBoundary');
        scope.setContext('errorBoundary', {
          componentStack: errorInfo.componentStack,
          errorBoundary: true
        });
        window.Sentry.captureException(error);
      });
    }

    // Also log to console in development
    if (import.meta.env.DEV) {
      console.error('Admin Error Boundary caught an error:', error, errorInfo);
    }
  }

  handleRetry = () => {
    // Clear error state to retry rendering
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  handleGoHome = () => {
    // Navigate back to admin dashboard
    window.location.href = '/admin/dashboard';
  };

  handleGoToMainApp = () => {
    // Navigate back to main application
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <CardTitle className="text-xl text-red-900">
                Something went wrong in the admin panel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  An unexpected error occurred while loading this admin page. 
                  The error has been logged and will be investigated.
                </p>
              </div>

              {/* Error details for development */}
              {import.meta.env.DEV && error && (
                <div className="bg-gray-50 rounded-lg p-4 text-left">
                  <h3 className="font-semibold text-sm text-gray-900 mb-2">
                    Development Error Details:
                  </h3>
                  <div className="text-xs text-gray-700 font-mono">
                    <div className="mb-2">
                      <strong>Error:</strong> {error.toString()}
                    </div>
                    {errorInfo && (
                      <div>
                        <strong>Component Stack:</strong>
                        <pre className="whitespace-pre-wrap mt-1 text-xs">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  onClick={this.handleRetry} 
                  className="flex items-center space-x-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Try Again</span>
                </Button>
                <Button 
                  onClick={this.handleGoHome} 
                  variant="outline"
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Admin Dashboard</span>
                </Button>
                <Button 
                  onClick={this.handleGoToMainApp} 
                  variant="outline"
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Main App</span>
                </Button>
              </div>

              {/* Additional help */}
              <div className="text-center text-xs text-muted-foreground">
                If this problem persists, please contact support or try refreshing the page.
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // If no error, render children normally
    return this.props.children;
  }
}

export default AdminErrorBoundary;